import { displayScale, isImageFile, loadImageFile } from "./imageEditorLogic";

export { displayScale, isImageFile, loadImageFile };

export type RgbColor = {
  r: number;
  g: number;
  b: number;
  a: number;
};

export type PickedColor = RgbColor & {
  x: number;
  y: number;
};

export function rgbToHex(color: RgbColor, includeAlpha = false): string {
  const hex = (n: number) => n.toString(16).padStart(2, "0");
  if (includeAlpha && color.a < 255) {
    return `#${hex(color.r)}${hex(color.g)}${hex(color.b)}${hex(color.a)}`.toUpperCase();
  }
  return `#${hex(color.r)}${hex(color.g)}${hex(color.b)}`.toUpperCase();
}

export function rgbToRgbString(color: RgbColor): string {
  if (color.a < 255) {
    return `rgba(${color.r}, ${color.g}, ${color.b}, ${(color.a / 255).toFixed(2)})`;
  }
  return `rgb(${color.r}, ${color.g}, ${color.b})`;
}

export function rgbToHsl({ r, g, b }: RgbColor): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l: Math.round(l * 100) };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;

  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

export function rgbToHslString(color: RgbColor): string {
  const { h, s, l } = rgbToHsl(color);
  return `hsl(${h}, ${s}%, ${l}%)`;
}

export function colorsEqual(a: RgbColor, b: RgbColor): boolean {
  return a.r === b.r && a.g === b.g && a.b === b.b && a.a === b.a;
}

export function colorCss(color: RgbColor): string {
  return rgbToRgbString(color);
}

export async function imageToPixelData(img: HTMLImageElement): Promise<ImageData> {
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("Canvas not supported");
  ctx.drawImage(img, 0, 0);
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

export function samplePixel(
  data: ImageData,
  x: number,
  y: number,
): RgbColor | null {
  if (x < 0 || y < 0 || x >= data.width || y >= data.height) return null;
  const i = (y * data.width + x) * 4;
  return {
    r: data.data[i],
    g: data.data[i + 1],
    b: data.data[i + 2],
    a: data.data[i + 3],
  };
}

export function clientToImageCoords(
  clientX: number,
  clientY: number,
  rect: DOMRect,
  imgW: number,
  imgH: number,
): { x: number; y: number } {
  const relX = (clientX - rect.left) / rect.width;
  const relY = (clientY - rect.top) / rect.height;
  return {
    x: Math.min(imgW - 1, Math.max(0, Math.floor(relX * imgW))),
    y: Math.min(imgH - 1, Math.max(0, Math.floor(relY * imgH))),
  };
}

export async function copyText(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
}

export function imageFileFromClipboard(
  clipboard: DataTransfer | null,
): File | null {
  if (!clipboard) return null;
  for (const item of clipboard.items) {
    if (item.type.startsWith("image/")) {
      return item.getAsFile();
    }
  }
  return null;
}
